import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2, Square, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Required fields for step completion
const REQUIRED_FIELDS = [
  "name", "address", "email", "phone", "communication_preference",
  "q1_claimants", "q2_defendants", "q3_desired_outcome", "q4_damages",
  "q5_other_damages", "q6_impact", "q7_simple_story"
];

// Get missing fields for current step
function getMissingFields(formData) {
  return REQUIRED_FIELDS.filter((k) => {
    const v = formData?.[k];
    return !v || (typeof v === "string" && v.trim().length === 0);
  });
}

// Audio manager - handles all audio operations
class AudioManager {
  constructor() {
    this.ctx = null;
    this.unlocked = false;
    this.audioElement = null;
    this.source = null;
  }

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.ctx;
  }

  async unlock() {
    if (this.unlocked) return true;
    
    const ctx = this.init();
    
    try {
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // Play silent buffer
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      
      this.unlocked = ctx.state === 'running';
      return this.unlocked;
    } catch (e) {
      return false;
    }
  }

  async playBase64Audio(base64Data) {
    try {
      await this.unlock();
      
      // Method 1: Try HTML Audio element
      if (this.audioElement) {
        this.audioElement.pause();
      }
      
      this.audioElement = new Audio(`data:audio/mp3;base64,${base64Data}`);
      this.audioElement.volume = 1;
      
      await this.audioElement.play();
      return { success: true, element: this.audioElement };
    } catch (e) {
      console.log('Audio play failed:', e.message);
      return { success: false, error: e.message };
    }
  }
}

const audioManager = new AudioManager();

// Start trying to unlock immediately
audioManager.init();

export const ChatInterface = ({ 
  sessionId, 
  formData, 
  onFieldUpdate, 
  onFormComplete,
  onStepComplete  // New prop to notify parent when step is complete
}) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Welcome to Quantum LAW Consulting's Claims Intake. I'm here to help you complete your intake form. You can see the form on the right side of your screen. Press the microphone icon below to speak with me, or type your response. Let's begin - what is your full legal name?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showNextNudge, setShowNextNudge] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const welcomePlayedRef = useRef(false);
  const welcomeAudioRef = useRef(null);
  const preloadingRef = useRef(false);

  // Check step completion whenever formData changes
  useEffect(() => {
    const missingFields = getMissingFields(formData);
    const isComplete = missingFields.length === 0;
    
    if (isComplete) {
      setShowNextNudge(true);
      if (onStepComplete) {
        onStepComplete({ 
          stepKey: 'client_and_seven',
          missingFields: [],
          stepComplete: true
        });
      }
    } else {
      setShowNextNudge(false);
    }
  }, [formData, onStepComplete]);

  // Scroll to keep chat in view (scroll page to top)
  const scrollToChat = useCallback(() => {
    // Scroll the page to bring the chat interface into view at the top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Also scroll within the chat messages to show latest
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  useEffect(() => {
    scrollToChat();
  }, [messages, scrollToChat]);

  // Stop speaking function
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (welcomeAudioRef.current) {
      welcomeAudioRef.current.pause();
      welcomeAudioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  }, []);

  // Preload and play welcome audio
  useEffect(() => {
    let audioData = null;
    let playing = false;
    let retryTimer = null;
    
    const loadAudio = async () => {
      if (preloadingRef.current) return;
      preloadingRef.current = true;
      
      try {
        const response = await axios.post(`${API}/tts`, {
          text: messages[0].content,
          voice: "nova"
        });
        
        if (response.data.audio) {
          audioData = response.data.audio;
          console.log('Audio data loaded');
          tryPlay();
        }
      } catch (e) {
        console.log('Failed to load audio:', e);
        preloadingRef.current = false;
      }
    };
    
    const tryPlay = async () => {
      if (playing || welcomePlayedRef.current || !audioData || !audioEnabled) return;
      
      const result = await audioManager.playBase64Audio(audioData);
      
      if (result.success) {
        playing = true;
        welcomePlayedRef.current = true;
        setIsSpeaking(true);
        console.log('Audio now playing!');
        
        result.element.onended = () => {
          setIsSpeaking(false);
          playing = false;
        };
        
        if (retryTimer) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
      }
    };
    
    // Load audio
    loadAudio();
    
    // Keep retrying every 200ms
    retryTimer = setInterval(tryPlay, 200);
    
    // Also try on valid user gesture events only
    const handleEvent = () => tryPlay();
    
    // Only events that count as user gestures
    const events = [
      'click', 'mousedown', 'mouseup',
      'touchstart', 'touchend',
      'keydown', 'keyup',
      'pointerdown', 'pointerup'
    ];
    
    events.forEach(e => {
      document.addEventListener(e, handleEvent, { capture: true, passive: true });
    });
    
    return () => {
      if (retryTimer) clearInterval(retryTimer);
      events.forEach(e => {
        document.removeEventListener(e, handleEvent, { capture: true });
      });
    };
  }, [messages, audioEnabled]);

  // Play the preloaded welcome audio
  const playWelcomeAudio = useCallback(async () => {
    // This is now handled by the effect above
    return welcomePlayedRef.current;
  }, []);

  const speakText = useCallback(async (text) => {
    if (!audioEnabled || !text) return;
    
    // Stop any current speech first
    stopSpeaking();
    
    setIsSpeaking(true);
    try {
      const response = await axios.post(`${API}/tts`, {
        text: text,
        voice: "nova"
      });

      if (response.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsSpeaking(false);
          audioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsSpeaking(false);
          audioRef.current = null;
        };
        
        await audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  }, [audioEnabled, stopSpeaking]);

  const toggleAudio = () => {
    if (isSpeaking) {
      stopSpeaking();
    }
    setAudioEnabled(!audioEnabled);
  };

  const sendMessage = async (message) => {
    if (!message.trim() || isLoading) return;

    // Stop any current speech immediately
    stopSpeaking();

    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setConversationHistory(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: message,
        form_data: formData,
        conversation_history: conversationHistory
      });

      const { response: aiResponse, field_update, is_complete } = response.data;

      const assistantMessage = { role: 'assistant', content: aiResponse };
      setMessages(prev => [...prev, assistantMessage]);
      setConversationHistory(prev => [...prev, assistantMessage]);

      // Speak the response if audio is enabled and not recording
      if (audioEnabled && !isRecording) {
        await speakText(aiResponse);
      }

      if (field_update) {
        onFieldUpdate(field_update);
      }

      if (is_complete) {
        onFormComplete();
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = 'I apologize, but I encountered an error. Please try again or type your response.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const startRecording = async () => {
    // Stop any current speech immediately when user wants to talk
    stopSpeaking();
    
    // Don't start if already recording or processing
    if (isRecording || isLoading) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream, { 
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await transcribeAudio(audioBlob);
        }
        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
    } catch (error) {
      console.error('Recording error:', error);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/transcribe`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.text && response.data.text.trim()) {
        await sendMessage(response.data.text);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      const errorMsg = 'I had trouble understanding that. Please try speaking again or type your response.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
      setIsLoading(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="chat-container" data-testid="chat-interface">
      {/* Audio Status Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {isSpeaking && (
            <button 
              onClick={stopSpeaking}
              className="flex items-center gap-2 text-sm text-green-600 hover:text-green-800 cursor-pointer"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Speaking... (click to stop)
            </button>
          )}
          {isRecording && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Recording...
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleAudio}
          className="text-muted-foreground"
          data-testid="toggle-audio-btn"
        >
          {audioEnabled ? (
            <>
              <Volume2 className="w-4 h-4 mr-1" />
              Audio On
            </>
          ) : (
            <>
              <VolumeX className="w-4 h-4 mr-1" />
              Audio Off
            </>
          )}
        </Button>
      </div>

      <div className="chat-messages" data-testid="chat-messages">
        {messages.map((msg, index) => (
          <div 
            key={index} 
            className={`chat-message ${msg.role}`}
            data-testid={`chat-message-${msg.role}-${index}`}
          >
            {msg.content}
          </div>
        ))}
        {isLoading && (
          <div className="chat-message assistant">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-input-container">
        <Button
          variant={isRecording ? "destructive" : "outline"}
          size="icon"
          onClick={toggleRecording}
          disabled={isLoading}
          className={`voice-btn ${isRecording ? 'recording' : ''}`}
          data-testid="voice-input-btn"
          title={isRecording ? "Stop recording" : "Start recording"}
        >
          {isRecording ? (
            <Square className="w-4 h-4" />
          ) : (
            <Mic className="w-4 h-4" />
          )}
        </Button>
        
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your response..."
          disabled={isLoading || isRecording}
          className="rounded-none"
          data-testid="chat-input"
        />
        
        <Button
          onClick={() => sendMessage(inputValue)}
          disabled={!inputValue.trim() || isLoading}
          className="rounded-none bg-black text-white hover:bg-black/90"
          data-testid="send-btn"
        >
          <Send className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Step Complete Nudge - Shows when all required fields are filled */}
      {showNextNudge && (
        <div className="bg-green-50 border-t border-green-200 px-4 py-3" data-testid="next-nudge">
          <div className="flex items-center gap-2 text-green-800">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">
              All fields complete! Verify the form on the right, then click
            </span>
            <span className="inline-flex items-center gap-1 bg-green-600 text-white px-2 py-1 rounded text-xs font-bold animate-pulse">
              NEXT <ChevronRight className="w-3 h-3" />
            </span>
            <span className="text-sm font-medium">in the lower-right.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
